import React, { useEffect, useRef, useState } from 'react';
import BattleSprite from './BattleSprite';
import BattleSkillEffect from './BattleSkillEffect';
import {
  approachDurationMs,
  dropDurationMs,
  dropDistancePx,
  isMetinEncounter,
  metinStoneSheet,
  metinConfigId,
  preloadMetinStoneImage,
  METIN_DROP_FPS,
  METIN_ROW_DROP,
  METIN_ROW_IDLE,
  METIN_IDLE_FPS,
  METIN_ROW_DEATH,
  METIN_DROP_SOUND,
} from '../utils/battleSprites';
import { playSfxFile } from '../utils/audioSettings';
import { formatDamageTypeLabel } from '../utils/armorEfsunUtils';
import { METIN_FEET_OFFSET_X, MONSTER_ATTACK_STANDOFF_PX } from '../constants/battleArenaConfig';
import { tl } from '../utils/inlineI18n';

/** Karakter dibinde hafif yan yana kayma (px) */
const CENTER_OFFSETS = [-20, -10, 0, 10, 20];

function slotBottom(slotIndex) {
  return 10 + (slotIndex % 3) * 3;
}

function centerX(slotIndex, knockbackPx) {
  const base = CENTER_OFFSETS[slotIndex % CENTER_OFFSETS.length];
  return base + (knockbackPx || 0);
}

function feetX(slotIndex, side, knockbackPx = 0, standoffExtra = 0) {
  if (side === 'center') {
    return centerX(slotIndex, knockbackPx);
  }
  const extra = side === 'right' ? standoffExtra : -standoffExtra;
  const standoff = (side === 'right'
    ? MONSTER_ATTACK_STANDOFF_PX
    : -MONSTER_ATTACK_STANDOFF_PX) + extra;
  return centerX(slotIndex, knockbackPx) + standoff;
}

// Vuruş efektleri (yanıp sönme, kıvılcım, sarsılma) ve skill tozu ne kadar sürer.
const HIT_FX_MS = 400;
const SKILL_DUST_MS = 900;

function getDamageFloatClass(float, taken = false) {
  if (!float) return taken ? 'floating-damage-red' : 'floating-damage-green';
  if (float.heal) return 'floating-damage-heal';
  if (float.critical && float.pierced) return 'floating-damage--crit-pierce';
  if (float.critical) return 'floating-damage--critical';
  if (float.pierced) return 'floating-damage--pierce';
  return taken ? 'floating-damage-red' : 'floating-damage-green';
}

function ArenaOverlayBar({
  current,
  max,
  fillClassName = '',
  floats = [],
  suffix = '',
  pendingAmount = 0,
  pendingClassName = 'hp-pending',
}) {
  const safeMax = Math.max(0, max || 0);
  const safeCurrent = Math.max(0, current || 0);
  const pct = safeMax > 0 ? Math.max(0, Math.min(100, (safeCurrent / safeMax) * 100)) : 0;
  const pendingWidthPct = safeMax > 0
    ? (Math.min(Math.max(0, pendingAmount), Math.max(0, safeMax - safeCurrent)) / safeMax) * 100
    : 0;
  return (
    <div className="battle-character-bar-wrap">
      {floats.map((f) => (
        <div
          key={f.id}
          className={[
            'battle-character-bar-float',
            f.heal ? 'battle-character-bar-float--heal' : 'battle-character-bar-float--dmg',
            suffix ? 'battle-character-bar-float--mp' : '',
          ].filter(Boolean).join(' ')}
        >
          {f.heal ? '+' : '-'}{f.value}{suffix}
        </div>
      ))}
      <div className="hp-bar-container battle-monster-hp-bar">
        <div className={`hp-bar-fill ${fillClassName}`.trim()} style={{ width: `${pct}%` }} />
        {pendingWidthPct > 0 && (
          <div
            className={`bar-pending ${pendingClassName}`}
            style={{ left: `${pct}%`, width: `${pendingWidthPct}%` }}
          />
        )}
        <span className="battle-monster-hp-text">
          {safeCurrent} / {safeMax}
        </span>
      </div>
    </div>
  );
}

// Arena rakibinin çubuklarında +N / -N sayıları (canavarlarda floats verilmez, sarmalayıcı eklenmez).
function BarFloatWrap({ floats, suffix = '', children }) {
  if (!Array.isArray(floats)) return children;
  return (
    <div className="battle-character-bar-wrap">
      {floats.map((f) => (
        <div
          key={f.id}
          className={[
            'battle-character-bar-float',
            f.heal ? 'battle-character-bar-float--heal' : 'battle-character-bar-float--dmg',
            suffix ? 'battle-character-bar-float--mp' : '',
          ].filter(Boolean).join(' ')}
        >
          {f.heal ? '+' : '-'}{f.value}{suffix}
        </div>
      ))}
      {children}
    </div>
  );
}

const BattleArena = ({
  monsters,
  monsterDamageFloats,
  characterBattleSprites,
  characterLabel = 'K',
  characterLevel = 0,
  characterAnim = 'normal',
  characterWalkDir = null,
  characterHP,
  characterMaxHP = 0,
  characterMP = 0,
  characterMaxMP = 0,
  characterHpFloats = [],
  characterDamageFloat = null,
  characterMpFloats = [],
  characterHpPending = 0,
  characterMpPending = 0,
  characterDeathActive = false,
  characterAnchorRef = null,
  characterShiftX = 0,
  skillVisualEffects = {},
  onSkillEffectComplete,
  onApproachComplete,
  onMonsterDeathComplete,
  onMonsterSkillEffectComplete,
  emptyLabel = tl('Canavar yok. "Canavar Çek" butonuna basın.', 'No monsters. Press "Pull Monsters".'),
  hideMonstersWhenDead = true,
}) => {
  const isDead = characterDeathActive || characterHP <= 0;
  const characterNormalSprite = characterBattleSprites?.normalSprite || characterBattleSprites?.baseSprite;
  const characterAttackSprite = characterBattleSprites?.attackSprite;
  const characterCurrentSprite =
    characterAnim === 'attack' && characterAttackSprite
      ? characterAttackSprite
      : characterNormalSprite;
  const approachTimersRef = useRef(new Map());
  const predropSoundPlayedRef = useRef(new Set());
  const [metinImagesReady, setMetinImagesReady] = useState(() => new Set());

  useEffect(() => {
    monsters.forEach((m) => {
      if (!isMetinEncounter(m) || m.spawnPhase !== 'predrop') return;
      preloadMetinStoneImage(metinConfigId(m)).then(() => {
        setMetinImagesReady((prev) => {
          if (prev.has(m.id)) return prev;
          const next = new Set(prev);
          next.add(m.id);
          return next;
        });
      });
    });
  }, [monsters]);

  useEffect(() => {
    const aliveIds = new Set(monsters.map((m) => m.id));

    approachTimersRef.current.forEach((timerId, id) => {
      if (!aliveIds.has(id)) {
        clearTimeout(timerId);
        approachTimersRef.current.delete(id);
      }
    });

    predropSoundPlayedRef.current.forEach((id) => {
      if (!aliveIds.has(id)) predropSoundPlayedRef.current.delete(id);
    });

    monsters.forEach((m) => {
      if (m.spawnPhase === 'predrop' && !predropSoundPlayedRef.current.has(m.id)) {
        predropSoundPlayedRef.current.add(m.id);
        playSfxFile(METIN_DROP_SOUND, 1, 'character');
      }

      const isSpawning = m.spawnPhase === 'approaching'
        || m.spawnPhase === 'predrop'
        || m.spawnPhase === 'dropping';
      if (!isSpawning) {
        const pending = approachTimersRef.current.get(m.id);
        if (pending) {
          clearTimeout(pending);
          approachTimersRef.current.delete(m.id);
        }
        return;
      }
      if (approachTimersRef.current.has(m.id)) return;

      if (m.spawnPhase === 'predrop' && !metinImagesReady.has(m.id)) {
        return;
      }

      const ms = m.spawnPhase === 'predrop'
        ? 0
        : m.spawnPhase === 'dropping'
          ? dropDurationMs()
          : approachDurationMs(m.battleSprites?.run);
      const timerId = setTimeout(() => {
        approachTimersRef.current.delete(m.id);
        if (onApproachComplete) onApproachComplete(m.id);
      }, ms);
      approachTimersRef.current.set(m.id, timerId);
    });
  }, [monsters, onApproachComplete, metinImagesReady]);

  useEffect(
    () => () => {
      approachTimersRef.current.forEach(clearTimeout);
      approachTimersRef.current.clear();
    },
    []
  );

  const getMonsterSprite = (m) => {
    if (isMetinEncounter(m)) {
      const configId = metinConfigId(m);
      if (m.anim === 'death') return metinStoneSheet(configId, METIN_ROW_DEATH);
      if (m.spawnPhase === 'dropping') return metinStoneSheet(configId, METIN_ROW_DROP, METIN_DROP_FPS);
      return metinStoneSheet(configId, METIN_ROW_IDLE, METIN_IDLE_FPS);
    }
    const bs = m.battleSprites;
    if (m.anim === 'death' && bs?.death) return bs.death;
    if (m.anim === 'attack' && bs?.attack) return bs.attack;
    if (m.spawnPhase === 'dropping' && (bs?.attack || bs?.run)) return bs.attack || bs.run;
    if (bs?.run) return bs.run;
    return null;
  };

  const isAliveMonster = (m) => !m.removeAt && (m.hp == null || m.hp > 0) && m.anim !== 'death';
  const monsterSideCounts = monsters.reduce(
    (counts, m) => {
      if (!isAliveMonster(m)) return counts;
      const side = m.originSide || m.side || 'left';
      if (side === 'center') return counts;
      return { ...counts, [side]: (counts[side] || 0) + 1 };
    },
    { left: 0, right: 0 }
  );
  const characterFacing = characterWalkDir
    || (monsterSideCounts.left > monsterSideCounts.right ? 'left' : 'right');

  // Vuruş hissi: son vuruşta ekran hafifçe sarsılır (kritikte biraz daha güçlü).
  // Animasyon adı A/B diye değişir ki art arda vuruşlarda yeniden başlasın.
  const nowMs = Date.now();
  let lastHit = null;
  Object.values(monsterDamageFloats || {}).forEach((f) => {
    if (f && !f.heal && f.createdAt && (!lastHit || f.createdAt > lastHit.createdAt)) lastHit = f;
  });
  const shakeActive = lastHit && nowMs - lastHit.createdAt < HIT_FX_MS;
  const shakeStyle = shakeActive
    ? {
      animation: `${lastHit.critical ? 'battleCritShake' : 'battleHitShake'}${lastHit.createdAt % 2 ? 'A' : 'B'} ${lastHit.critical ? 260 : 180}ms ease-out`,
    }
    : undefined;
  // Skill atınca karakterin ayağının dibinde toz bulutu kalkar.
  let lastSkillKey = 0;
  Object.values(skillVisualEffects || {}).forEach((e) => {
    if (e?.key > lastSkillKey) lastSkillKey = e.key;
  });
  const skillDustActive = lastSkillKey > 0 && nowMs - lastSkillKey < SKILL_DUST_MS;

  return (
    <div className="battle-arena" style={shakeStyle}>
      <div
        ref={characterAnchorRef}
        className={[
          'battle-character-center',
          `battle-character--facing-${characterFacing}`,
          characterAnim === 'attack' ? 'battle-character-center--attack' : '',
          characterWalkDir ? 'battle-character-center--walking' : '',
        ].filter(Boolean).join(' ')}
        style={characterShiftX ? { left: `calc(50% + ${characterShiftX}px)` } : undefined}
      >
        <div className="battle-character-bars">
          {characterLabel ? (
            <span className="battle-character-name" title={characterLabel}>
              {characterLevel > 0 ? <span className="battle-name-level">Lv.{characterLevel}</span> : null}
              {characterLabel}
            </span>
          ) : null}
          <ArenaOverlayBar
            current={characterHP}
            max={characterMaxHP}
            floats={characterHpFloats}
            pendingAmount={characterHpPending}
            pendingClassName="hp-pending"
          />
          <ArenaOverlayBar
            current={characterMP}
            max={characterMaxMP}
            fillClassName="battle-character-mp-fill"
            floats={characterMpFloats}
            suffix=" MP"
            pendingAmount={characterMpPending}
            pendingClassName="mp-pending"
          />
        </div>
        {characterDamageFloat ? (
          <div
            key={characterDamageFloat.id}
            className={`floating-damage ${getDamageFloatClass(characterDamageFloat, true)} battle-monster-float`}
          >
            {characterDamageFloat.heal ? '+' : '-'}{characterDamageFloat.value}
          </div>
        ) : null}
        {isDead ? (
          <BattleSprite
            sheet={characterBattleSprites?.deathSprite}
            playing
            loop={false}
            fallbackLabel={characterLabel}
            className="battle-character-layer battle-character-fallback-wrap"
          />
        ) : (
          <>
            {Object.entries(skillVisualEffects).map(([skillId, eff]) => (
              <div
                key={`skill-fx-${skillId}-${eff.key}`}
                className="battle-skill-effect"
              >
                <BattleSkillEffect
                  sheet={eff.sheet}
                  loop={eff.loop}
                  untilMs={eff.untilMs}
                  onComplete={() => onSkillEffectComplete && onSkillEffectComplete(Number(skillId))}
                  onExpired={() => onSkillEffectComplete && onSkillEffectComplete(Number(skillId))}
                />
              </div>
            ))}
            <BattleSprite
              sheet={characterCurrentSprite}
              playing={characterAnim === 'attack' || Boolean(characterWalkDir)}
              loop={characterAnim !== 'attack' && Boolean(characterWalkDir)}
              fallbackLabel={characterLabel}
              className="battle-character-layer battle-character-base battle-character-fallback-wrap"
            />
            {characterWalkDir ? <span className="battle-walk-dust" aria-hidden="true" /> : null}
            {skillDustActive ? (
              <span key={`skill-dust-${lastSkillKey}`} className="battle-skill-dust" aria-hidden="true">
                <i /><i /><i /><i /><i />
              </span>
            ) : null}
          </>
        )}
      </div>

      {(isDead && hideMonstersWhenDead) ? null : monsters.length === 0 ? (
        emptyLabel ? <p className="battle-arena-empty">{emptyLabel}</p> : null
      ) : (
        monsters.map((m) => {
          const side = m.originSide || m.side || 'left';
          const slotIndex = m.slotIndex ?? 0;
          const metin = isMetinEncounter(m);
          const alive = isAliveMonster(m);
          const dying = m.anim === 'death' || m.removeAt;
          const approaching = alive && m.spawnPhase === 'approaching';
          const predrop = alive && m.spawnPhase === 'predrop';
          const dropping = alive && m.spawnPhase === 'dropping';
          const metinOffset = metin ? METIN_FEET_OFFSET_X : 0;
          const feetExtra = m.standoffExtra || 0;
          const feetXVal = feetX(slotIndex, side, 0, feetExtra) + metinOffset;
          const totalX = feetX(slotIndex, side, m.knockbackPx || 0, feetExtra) + metinOffset;
          const knocked = (alive || dying) && Math.abs(m.knockbackPx || 0) > 0;
          const atFeet = alive && m.spawnPhase === 'ready';
          const returning = alive && atFeet && !knocked && m.knockbackMoveMs > 0;
          const useInlinePosition = (!approaching && !dropping) || knocked || predrop;
          const approachMs = approachDurationMs(m.battleSprites?.run);
          const dropMs = dropDurationMs();
          const moveMs = knocked ? (m.knockbackOutMs || 260) : (m.knockbackMoveMs || approachMs);
          const hpPct = m.maxHp > 0 ? Math.max(0, Math.min(100, (m.hp / m.maxHp) * 100)) : 0;
          const mpPct = m.maxMp > 0 ? Math.max(0, Math.min(100, ((m.mp || 0) / m.maxMp) * 100)) : 0;
          const sheet = getMonsterSprite(m);
          const metinIdleLoop = metin && atFeet && m.anim !== 'death';
          const loop = metinIdleLoop || (!metin && m.anim !== 'death' && !dropping);
          const metinIdlePulse = metinIdleLoop;
          const metinLabelsVisible = !metin || atFeet || dying;
          const metinSpriteVisible = !metin || dropping || atFeet || dying;
          const metinDeathLead = metin && dying && m.anim !== 'death';
          const hitFloat = monsterDamageFloats[m.id];
          const hitActive = Boolean(hitFloat && !hitFloat.heal && hitFloat.createdAt && nowMs - hitFloat.createdAt < HIT_FX_MS);
          const hitParity = hitActive && hitFloat.createdAt % 2 ? 'A' : 'B';
          // Kıvılcım canavarın boyuna göre (json'daki kare genişliği/yüksekliği) ölçeklenir.
          const sparkPx = hitActive
            ? Math.round(Math.max(6, Math.min(17, 0.225 * Math.min(sheet?.frameWidth || 60, sheet?.frameHeight || 60)))
              * (hitFloat.critical ? 1.25 : 1))
            : 0;
          const spritePlaying =
            metinSpriteVisible &&
            (alive || dying) &&
            !predrop &&
            !metinDeathLead &&
            (approaching ||
              dropping ||
              m.anim === 'attack' ||
              m.anim === 'run' ||
              m.anim === 'death' ||
              metinIdlePulse ||
              (atFeet && m.anim === 'idle'));

          return (
            <div
              // Ölen canavar aynı öğe olarak kalır: bulunduğu yerden kayarak geri savrulur.
              // (Eskiden yeni öğe olarak savrulmuş konumda birden beliriyordu: "uzakta ölüyor".)
              key={`m-${m.id}`}
              className={[
                'battle-monster-slot',
                `battle-monster--${side}`,
                approaching ? 'battle-monster--approaching' : '',
                predrop ? 'battle-monster--predrop' : '',
                dropping ? 'battle-monster--dropping' : '',
                atFeet ? 'battle-monster--at-feet' : '',
                knocked ? 'battle-monster--knocked-back' : '',
                returning ? 'battle-monster--returning' : '',
                alive ? 'battle-monster--alive' : '',
                dying ? 'battle-monster--dying' : '',
                metin && predrop ? 'battle-monster--metin-predrop' : '',
              ].filter(Boolean).join(' ')}
              style={{
                bottom: `${slotBottom(slotIndex)}px`,
                '--feet-x': `${feetXVal}px`,
                '--edge-x': side === 'right' ? 'min(46vw, 210px)' : 'max(-46vw, -210px)',
                '--kb-tilt': side === 'right' ? '5deg' : '-5deg',
                '--monster-move-ms': `${moveMs}ms`,
                ...(approaching
                  ? { '--approach-duration': `${approachMs}ms` }
                  : {}),
                ...(predrop || dropping
                  ? {
                    '--drop-height': `${dropDistancePx()}px`,
                  }
                  : {}),
                ...(dropping
                  ? {
                    '--drop-duration': `${dropMs}ms`,
                  }
                  : {}),
                ...(useInlinePosition
                  ? { transform: `translateX(calc(-50% + ${totalX}px))` }
                  : {}),
              }}
            >
              {metinLabelsVisible && monsterDamageFloats[m.id] && (
                <div
                  key={monsterDamageFloats[m.id].id ?? monsterDamageFloats[m.id].createdAt ?? m.id}
                  className={`floating-damage ${getDamageFloatClass(monsterDamageFloats[m.id])} battle-monster-float`}
                >
                  {monsterDamageFloats[m.id].heal ? '+' : '-'}{monsterDamageFloats[m.id].value}
                </div>
              )}
              {metinLabelsVisible ? (
                <>
                  <span
                    className={[
                      'battle-monster-name',
                      metin ? 'battle-monster-name--metin' : '',
                    ].filter(Boolean).join(' ')}
                    title={m.level > 0 ? `Lv.${m.level} ${m.name}` : m.name}
                  >
                    {m.level > 0 ? <span className="battle-name-level">Lv.{m.level}</span> : null}
                    {m.name}
                  </span>
                  <div className={`battle-monster-hp${m.maxMp > 0 ? ' battle-monster-hp--dual' : ''}`}>
                    <BarFloatWrap floats={m.hpFloats}>
                      <div className="hp-bar-container battle-monster-hp-bar">
                        <div className="hp-bar-fill" style={{ width: `${hpPct}%` }} />
                        <span className="battle-monster-hp-text">
                          {m.hp} / {m.maxHp}
                        </span>
                      </div>
                    </BarFloatWrap>
                    {m.maxMp > 0 && (
                      <BarFloatWrap floats={m.mpFloats} suffix=" MP">
                        <div className="hp-bar-container battle-monster-hp-bar">
                          <div className="hp-bar-fill battle-character-mp-fill" style={{ width: `${mpPct}%` }} />
                          <span className="battle-monster-hp-text">
                            {m.mp || 0} / {m.maxMp}
                          </span>
                        </div>
                      </BarFloatWrap>
                    )}
                    {m.damageType && m.damageType !== 'none' && (
                      <span className="battle-monster-damage-type">
                        {formatDamageTypeLabel(m.damageType)}
                      </span>
                    )}
                  </div>
                </>
              ) : null}
              <div
                className={`battle-monster-body ${!metinSpriteVisible ? 'battle-monster-body--hidden' : ''}`}
                style={hitActive ? { animation: `battleHitFlash${hitParity} 300ms ease-out` } : undefined}
              >
                {hitActive ? (
                  <span
                    key={`hit-${hitFloat.createdAt}`}
                    className="battle-hit-spark"
                    style={{ width: sparkPx, height: sparkPx, margin: `${-sparkPx / 2}px 0 0 ${-sparkPx / 2}px` }}
                    aria-hidden="true"
                  />
                ) : null}
                {m.skillEffect?.sheet ? (
                  <div className="battle-skill-effect">
                    <BattleSkillEffect
                      sheet={m.skillEffect.sheet}
                      loop={Boolean(m.skillEffect.loop)}
                      untilMs={m.skillEffect.untilMs || 0}
                      onComplete={() => onMonsterSkillEffectComplete && onMonsterSkillEffectComplete()}
                      onExpired={() => onMonsterSkillEffectComplete && onMonsterSkillEffectComplete()}
                    />
                  </div>
                ) : null}
                <BattleSprite
                  sheet={sheet}
                  playing={spritePlaying}
                  loop={loop}
                  hideUntilLoaded={metin}
                  fallbackLabel={m.name}
                  className={`battle-monster-sprite ${alive || dying ? 'battle-monster-sprite--active' : ''}`}
                  onComplete={
                    m.anim === 'death' && !metin
                      ? () => onMonsterDeathComplete && onMonsterDeathComplete(m)
                      : undefined
                  }
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default React.memo(BattleArena);
